import key from 'weak-key';
import { Carousel, Col, Row, Container } from '@geberit/gdds';

// styles
import { OkvTilesWrapper } from './online-catalog-assets-wrapper.styles';

// types
import type { OKVItem, OKVTile, OkvTilesProps } from './online-catalog-assets.types';

// components
import { Loader } from 'components/Loader/Loader';
import { ProductDeprecatedPlaceholder } from 'components/ContentElementsGdds/product-tiles';
import OkvTile from './okv-tile';

// utils
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { buildSize, gridSizes, themeToGridSize } from 'utils/gridSize';
import { isEmpty } from 'utils/is-empty';
import { useIsDesktop, useIsTablet } from 'components/App/SizeProvider';

const OkvTilesView = ({
  items,
  carousel,
  backgroundColor,
  size,
  isAccordion = false,
}: OkvTilesProps) => {
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsDesktop({ gdds: true });
  const isTablet = useIsTablet();
  const isPreview = useIsPreview();

  const isCarousel =
    carousel &&
    ((isLargeDesktop && items.length > 3) ||
      (isDesktop && items.length > 2) ||
      (isTablet && items.length > 2) ||
      (!isDesktop && !isTablet && items.length > 1));

  const isOKVTile = (item: OKVItem | OKVTile): item is OKVTile =>
    (item as OKVTile)?.product !== undefined;

  const renderCarousel = (children) => {
    if (isCarousel) {
      return (
        <Col size={[4, 8, 12]} className="col">
          <Carousel
            responsiveLayout={{
              small: { slides: 1, slidesToMove: 1 },
              medium: { slides: 2, slidesToMove: 1 },
              large: { slides: 3, slidesToMove: 1 },
              xlarge: { slides: 3, slidesToMove: 1 },
            }}
            hideButtons
            supportMouse
          >
            {children}
          </Carousel>
        </Col>
      );
    }

    return children;
  };

  return (
    <OkvTilesWrapper isCarousel={isCarousel} backgroundColor={backgroundColor}>
      <Container
        maxContentWidth={buildSize(
          gridSizes[isCarousel ? themeToGridSize.NORD_CAROUSEL : 'gddsFullGrid'],
        )}
      >
        <Row className="row">
          {!isEmpty(items) &&
            renderCarousel(
              items.map((item) => {
                if (!isOKVTile(item)) {
                  return (
                    <Col size={size} key={key(item)} className="tile">
                      <div className="loader">
                        <Loader size="small" />
                      </div>
                    </Col>
                  );
                }

                if (isPreview && item.product.deprecated) {
                  return (
                    <Col size={size} key={key(item)} className="tile">
                      <ProductDeprecatedPlaceholder
                        key={item.identifier}
                        tileType={item.type}
                        product={item.identifier}
                      />
                    </Col>
                  );
                }

                return isCarousel ? (
                  <div className="carousel" key={key(item)}>
                    <OkvTile
                      {...item}
                      backgroundColor={backgroundColor}
                      isAccordion={isAccordion}
                    />
                  </div>
                ) : (
                  <Col size={size} key={key(item)}>
                    <OkvTile
                      {...item}
                      backgroundColor={backgroundColor}
                      isAccordion={isAccordion}
                    />
                  </Col>
                );
              }),
            )}
        </Row>
      </Container>
    </OkvTilesWrapper>
  );
};

export default OkvTilesView;
